import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { buildRoleLabelMap, getDefaultRoleLabel } from './roleLabels';
import type { CompanyRoleSetting, UserRole } from '@/apolloClient.types';
import { useSelectedCompanyId, useUser } from '@/contexts/UserSession';
import { useFetchCompanyDetailsQuery } from '@/pages/CompanyManagementPage/shared/Companies.api';

export interface CompanyRoleLabelsHook {
    getRoleLabel: (role: UserRole) => string;
    getCompanyRoles: () => UserRole[];
    roleLabelsMap: Record<UserRole, string>;
    loading: boolean;
    companyId: string | null;
}

/**
 * Resolves role display labels for the current context company.
 * - For TenantUser, uses their company id
 * - For SuperAdmin, uses selectedCompanyId from session (if any)
 * Falls back to i18n or default labels when company settings are unavailable.
 */
const useCompanyRoleLabels = (): CompanyRoleLabelsHook => {
    // This hook is used in many places that can mount on interaction (e.g. disclosure expand).
    // Avoid i18n Suspense here so we don't trigger the route-level <Suspense fallback={<Loader/>}>.
    const { t } = useTranslation('userManagementPage', { useSuspense: false });
    const user = useUser(true);
    const selectedCompanyId = useSelectedCompanyId();

    const companyId: string | null = useMemo(() => {
        if (user?.__typename === 'TenantUser' && user.company) {
            return user.company.id;
        }
        return selectedCompanyId || null;
    }, [user, selectedCompanyId]);

    const { data, loading } = useFetchCompanyDetailsQuery({
        skip: !companyId,
        variables: { id: companyId as string },
        fetchPolicy: 'cache-first',
    });

    const roleLabelsMap = useMemo(() => {
        const companyRoleSettings: CompanyRoleSetting[] = data?.company?.roleSettings ?? [];
        return buildRoleLabelMap(companyRoleSettings, t);
    }, [data, t]);

    const getCompanyRoles = useCallback((): UserRole[] => {
        return data?.company?.roleSettings?.filter(setting => setting.isEnabled).map(setting => setting.role) ?? [];
    }, [data]);

    const getRoleLabel = useCallback(
        (role: UserRole): string => roleLabelsMap[role] ?? getDefaultRoleLabel(role),
        [roleLabelsMap]
    );

    return { getRoleLabel, getCompanyRoles, roleLabelsMap, loading: !!companyId && loading, companyId };
};

export default useCompanyRoleLabels;
